import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

const ROLE_LABELS = { owner: "Власник", editor: "Редактор", viewer: "Глядач" };
const ROLE_COLORS = { owner: "#6366f1", editor: "#f59e0b", viewer: "#64748b" };
const ROLES = ["owner", "editor", "viewer"];

export default function ProjectMembers({ projectId }) {
  const { apiFetch, t, dark, inp, currentUser } = useApp();
  const [members, setMembers] = useState([]);
  const [available, setAvailable] = useState([]);
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("editor");
  const [loading, setLoading] = useState(true);
  const [myRole, setMyRole] = useState("viewer");

  const load = async () => {
    try {
      const r = await apiFetch(`/api/projects/${projectId}/members`);
      const data = await r.json();
      if (Array.isArray(data)) {
        setMembers(data);
        const me = data.find(m => m.username === currentUser);
        if (me) setMyRole(me.role);
      }
    } catch {}
    setLoading(false);
  };

  const loadAvailable = async () => {
    try {
      const r = await apiFetch(`/api/projects/${projectId}/members/available`);
      const data = await r.json();
      if (Array.isArray(data)) setAvailable(data);
    } catch {}
  };

  useEffect(() => { load(); }, [projectId]);
  useEffect(() => { if (myRole === "owner") loadAvailable(); }, [myRole, projectId]);

  const isOwner = myRole === "owner";

  const changeRole = async (userId, role) => {
    try {
      const r = await apiFetch(`/api/projects/${projectId}/members/${userId}`, {
        method: "PUT", body: JSON.stringify({ role })
      });
      const d = await r.json();
      if (d.error) { alert(d.error); return; }
      load();
    } catch {}
  };

  const removeMember = async (userId) => {
    if (!confirm("Видалити учасника?")) return;
    try {
      const r = await apiFetch(`/api/projects/${projectId}/members/${userId}`, { method: "DELETE" });
      const d = await r.json();
      if (d.error) { alert(d.error); return; }
      load();
      loadAvailable();
    } catch {}
  };

  const addMember = async () => {
    if (!addUserId) return;
    try {
      await apiFetch(`/api/projects/${projectId}/members`, {
        method: "POST", body: JSON.stringify({ userId: parseInt(addUserId, 10), role: addRole })
      });
      setAddUserId("");
      load();
      loadAvailable();
    } catch {}
  };

  if (loading) return null;

  const selectStyle = {
    ...inp,
    fontSize: 11,
    padding: "5px 8px",
    width: "auto",
    minWidth: 90,
    cursor: "pointer",
    appearance: "auto",
  };

  return (
    <div style={{ marginTop: 14, padding: "12px 0 0", borderTop: `1px solid ${t.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Учасники проекту
      </div>

      {members.map(m => (
        <div key={m.user_id} style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "6px 10px", borderRadius: 8, marginBottom: 4,
          background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
          border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: ROLE_COLORS[m.role],
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 11, flexShrink: 0
          }}>
            {(m.full_name || m.username)[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.text }}>{m.full_name || m.username}</div>
            {m.full_name && <div style={{ fontSize: 10, color: t.textMuted }}>{m.username}</div>}
          </div>

          {isOwner ? (
            <>
              <select
                value={m.role}
                onChange={e => changeRole(m.user_id, e.target.value)}
                style={selectStyle}
              >
                {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
              <button
                onClick={() => removeMember(m.user_id)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 14, color: "#ef4444", padding: "2px 4px", lineHeight: 1
                }}
                title="Видалити"
              >x</button>
            </>
          ) : (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 6,
              background: ROLE_COLORS[m.role] + "18", color: ROLE_COLORS[m.role],
              border: `1px solid ${ROLE_COLORS[m.role]}30`
            }}>
              {ROLE_LABELS[m.role]}
            </span>
          )}
        </div>
      ))}

      {isOwner && available.length > 0 && (
        <div style={{
          display: "flex", gap: 6, marginTop: 8, alignItems: "center", flexWrap: "wrap"
        }}>
          <select
            value={addUserId}
            onChange={e => setAddUserId(e.target.value)}
            style={{ ...selectStyle, flex: 1, minWidth: 140 }}
          >
            <option value="">+ Додати учасника...</option>
            {available.map(u => (
              <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
            ))}
          </select>
          <select value={addRole} onChange={e => setAddRole(e.target.value)} style={selectStyle}>
            {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <button
            onClick={addMember}
            disabled={!addUserId}
            style={{
              background: addUserId ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : (dark ? "#333" : "#e5e7eb"),
              color: addUserId ? "#fff" : t.textMuted,
              border: "none", borderRadius: 8, padding: "6px 14px",
              fontSize: 11, fontWeight: 600, cursor: addUserId ? "pointer" : "default"
            }}
          >Додати</button>
        </div>
      )}
    </div>
  );
}
